import React, { useState, useRef, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    IconButton,
    RadioGroup,
    FormControlLabel,
    Radio,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import ChapterPriorityEditor from "./ChapterPriorityEditor";
import ConfirmDialog from "../../components/ConfirmDialog";
import AssignToField from "./AssignToField";
import TeacherDigestModal from "./TeacherDigestModal";
import ManualChapterPicker from "./ManualChapterPicker";
import RoundWizard from "./RoundWizard";
import { Field } from "./prepUi";
import { fmtNyDateTimeDay, nyDateInput, nyDateTimeInput } from "./prepDates";
import { QUESTION_MODES, WeaknessSourceField, cadenceSummary } from "./prepRoundShared";

// Round creation (one-off + recurring) lives in RoundWizard — the step-based flow. This file
// keeps the tables and the EDIT dialog for an existing round (a flat form: edits are usually
// one-field corrections, and released rounds carry extra constraints the wizard doesn't).

const emptyRound = {
    release_date: "", due_date: "", num_chapters: 2, questions_per_chapter: 30,
    mastery_threshold: 100, source_mode: "ALL", source_exam_ids: [],
    source_from_date: "", source_to_date: "", fallback_to_priority: true,
    assign_mode: "ALL", student_ids: [], notify_teachers_on_due: false,
    question_mode: "ADAPTIVE", set_size: 10, set_scope: "COHORT", manual_chapters: [], mastery_cycles: 2,
};

// Homework configuration for one SUBJECT program-course: rounds (explicit +
// recurring) and the fallback chapter priorities. The overview shows just the
// rounds table + actions; the add/recurrence forms open in dialogs so the page
// stays calm.
const HomeworkSection = ({ programUuid, subjectCourse, programExams, qbType, rounds, onChange }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [round, setRound] = useState(emptyRound);
    const [roundOpen, setRoundOpen] = useState(false); // EDIT dialog only — creation is the wizard
    const [roundEditId, setRoundEditId] = useState(null);
    const [wizard, setWizard] = useState(null); // { mode: "round" | "recurrence", editRule? }
    const [recurrences, setRecurrences] = useState([]);
    const [formError, setFormError] = useState("");
    const [busy, setBusy] = useState(false);
    const [digestRound, setDigestRound] = useState(null);
    const confirmRef = useRef();

    const courseRounds = (rounds || []).filter((r) => r.prep_program_course_id === subjectCourse.id);

    // Recurring rules for THIS subject course (the endpoint returns the whole program's).
    const loadRecurrences = useCallback(async () => {
        try {
            const res = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}/homework-recurrences`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setRecurrences((res.data || []).filter((r) => r.prep_program_course_id === subjectCourse.id));
        } catch (e) {
            setRecurrences([]);
        }
    }, [programUuid, subjectCourse.id]);
    useEffect(() => { loadRecurrences(); }, [loadRecurrences]);

    const setR = (patch) => setRound((prev) => ({ ...prev, ...patch }));

    const openEditRound = (r) => {
        setRound({
            release_date: nyDateTimeInput(r.release_date),
            due_date: nyDateTimeInput(r.due_date),
            num_chapters: r.num_chapters ?? 2,
            questions_per_chapter: r.questions_per_chapter ?? "",
            mastery_threshold: r.mastery_threshold ?? 100,
            source_mode: r.source_mode || "ALL",
            source_exam_ids: r.source_exam_ids || [],
            source_from_date: nyDateInput(r.source_from_date),
            source_to_date: nyDateInput(r.source_to_date),
            fallback_to_priority: r.fallback_to_priority ?? true,
            assign_mode: r.assign_mode || "ALL",
            student_ids: r.student_ids || [],
            notify_teachers_on_due: r.notify_teachers_on_due === true,
            question_mode: r.question_mode || "ADAPTIVE",
            set_size: r.set_size ?? 10,
            set_scope: r.set_scope || "COHORT",
            mastery_cycles: r.mastery_cycles ?? 2,
            manual_chapters: (r.manual_chapters || []).map((m) => ({ chapter_id: m.chapter_id, total_questions: m.total_questions ?? undefined })),
        });
        setRoundEditId(r.id);
        setFormError("");
        setRoundOpen(true);
    };
    const doDeleteRecurrence = async (ruleId) => {
        setBusy(true);
        try {
            await Axios.delete(`${base}/homework-recurrences/${ruleId}`, auth);
            loadRecurrences();
            if (onChange) onChange();
        } catch (e) {
            console.error("Delete recurrence error:", e);
        } finally {
            setBusy(false);
        }
    };
    const confirmDeleteRecurrence = (rule) => {
        confirmRef.current.open({
            title: "Delete recurring rule?",
            message: "Future unreleased rounds are removed. Released rounds are kept.",
            confirmLabel: "Delete rule",
            onConfirm: () => doDeleteRecurrence(rule.id),
        });
    };

    const saveRound = async () => {
        setBusy(true);
        setFormError("");
        const payload = {
            prep_program_course_id: subjectCourse.id,
            release_date: round.release_date || null,
            due_date: round.due_date || null,
            num_chapters: Number(round.num_chapters),
            questions_per_chapter: round.questions_per_chapter === "" ? null : Number(round.questions_per_chapter),
            mastery_threshold: Number(round.mastery_threshold),
            source_mode: round.source_mode,
            source_exam_ids: round.source_mode === "EXAMS" ? round.source_exam_ids : [],
            source_from_date: round.source_mode === "DATE_WINDOW" ? round.source_from_date || null : null,
            source_to_date: round.source_mode === "DATE_WINDOW" ? round.source_to_date || null : null,
            fallback_to_priority: round.fallback_to_priority,
            assign_mode: round.assign_mode || "ALL",
            student_ids: round.assign_mode === "SELECTED" ? round.student_ids : [],
            notify_teachers_on_due: round.notify_teachers_on_due === true,
            question_mode: round.question_mode,
            mastery_cycles: Number(round.mastery_cycles) || 2,
            ...(round.question_mode !== "ADAPTIVE" ? { manual_chapters: round.manual_chapters } : {}),
            ...(round.question_mode === "FIXED_SET"
                ? { set_size: Number(round.set_size), set_scope: round.set_scope, sampling: "RANDOM" }
                : {}),
        };
        try {
            await Axios.put(`${base}/homework-rounds/${roundEditId}`, payload, auth);
            setRound(emptyRound);
            setRoundEditId(null);
            setRoundOpen(false);
            if (onChange) onChange();
        } catch (e) {
            console.error("Save round error:", e);
            setFormError(e.response?.data?.error || "Failed to save round");
        } finally {
            setBusy(false);
        }
    };

    const doDeleteRound = async (roundId) => {
        setBusy(true);
        try {
            await Axios.delete(`${base}/homework-rounds/${roundId}`, auth);
            if (onChange) onChange();
        } catch (e) {
            console.error("Delete round error:", e);
        } finally {
            setBusy(false);
        }
    };

    const confirmDeleteRound = (roundId) => {
        confirmRef.current.open({
            title: "Delete round?",
            message: "Released rounds will cancel their ETT assignments. This cannot be undone.",
            confirmLabel: "Delete",
            onConfirm: () => doDeleteRound(roundId),
        });
    };

    const fmt = (d) => fmtNyDateTimeDay(d);

    return (
        <Box>
            {/* ---- Overview: rounds table + actions ---- */}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Homework rounds</Typography>
            {courseRounds.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    No rounds yet. Add a one-off round or set up a recurring schedule.
                </Typography>
            ) : (
                <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Release</TableCell>
                            <TableCell>Due</TableCell>
                            <TableCell>Chapters</TableCell>
                            <TableCell>Q/ch</TableCell>
                            <TableCell>Source</TableCell>
                            <TableCell>Assigned</TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {courseRounds.map((r) => (
                            <TableRow key={r.id}>
                                <TableCell>{fmt(r.release_date)}</TableCell>
                                <TableCell>{fmt(r.due_date)}</TableCell>
                                <TableCell>{r.num_chapters}</TableCell>
                                <TableCell>{r.questions_per_chapter ?? "—"}</TableCell>
                                <TableCell>{r.source_mode}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        color={r.assign_mode === "SELECTED" ? "warning" : "default"}
                                        label={r.assign_mode === "SELECTED"
                                            ? `${(r.student_ids || []).length} student${(r.student_ids || []).length === 1 ? "" : "s"}`
                                            : "All"}
                                    />
                                </TableCell>
                                <TableCell>
                                    {r.recurrence_id ? (
                                        <Tooltip title="Generated by the recurring-rounds rule">
                                            <Chip size="small" label="recurring" color="info" />
                                        </Tooltip>
                                    ) : null}
                                    {r.is_released ? <Chip size="small" label="released" color="success" sx={{ ml: 0.5 }} /> : null}
                                    {r.set_shortfall ? (
                                        <Tooltip title={`This round could only deal ${r.set_size - r.set_shortfall} of ${r.set_size} questions — the picked chapters are nearly out. Add chapters, or expect the next round to have nothing left to assign.`}>
                                            <Chip size="small" color="warning" variant="outlined" label="pool low" sx={{ ml: 0.5 }} />
                                        </Tooltip>
                                    ) : null}
                                    {r.is_released && r.notify_teachers_on_due ? (
                                        <Chip size="small" variant="outlined" label="results" clickable
                                            onClick={() => setDigestRound(r)} sx={{ ml: 0.5 }} />
                                    ) : null}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" onClick={() => openEditRound(r)} disabled={busy} aria-label="Edit round">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton size="small" onClick={() => confirmDeleteRound(r.id)} disabled={busy} aria-label="Delete round">
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setWizard({ mode: "round" })}>
                    Add round
                </Button>
                <Button variant="outlined" size="small" onClick={() => setWizard({ mode: "recurrence" })}>
                    Set up recurring rounds
                </Button>
            </Box>

            {/* ---- Recurring rules management (list / edit / delete) ---- */}
            {recurrences.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Recurring rules</Typography>
                    <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Cadence</TableCell>
                                <TableCell>Window</TableCell>
                                <TableCell>Rounds</TableCell>
                                <TableCell>Next release</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recurrences.map((rule) => (
                                <TableRow key={rule.id}>
                                    <TableCell>{cadenceSummary(rule)}</TableCell>
                                    <TableCell>{nyDateInput(rule.start_date)} → {nyDateInput(rule.end_date)}</TableCell>
                                    <TableCell>{rule.rounds_released ?? 0}/{rule.rounds_total ?? 0} released</TableCell>
                                    <TableCell>{rule.next_release ? fmtNyDateTimeDay(rule.next_release) : "—"}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => setWizard({ mode: "recurrence", editRule: rule })} disabled={busy} aria-label="Edit rule">
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => confirmDeleteRecurrence(rule)} disabled={busy} aria-label="Delete rule">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}

            {/* ---- Advanced: fallback chapter priorities (collapsed) ---- */}
            <Accordion sx={{ mt: 3, boxShadow: "none", border: "1px solid", borderColor: "divider", "&:before": { display: "none" } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box>
                        <Typography variant="subtitle2">Fallback chapter priorities</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Advanced — order used only when a student has no recent practice data.
                        </Typography>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <ChapterPriorityEditor programUuid={programUuid} programCourse={subjectCourse} qbType={qbType} />
                </AccordionDetails>
            </Accordion>

            {/* ---- Edit round dialog (creation lives in RoundWizard) ---- */}
            <Dialog open={roundOpen} onClose={() => setRoundOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit homework round</DialogTitle>
                <DialogContent dividers>
                    {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                        <Field label="Schedule (ET — America/New_York)">
                            <Box display="flex" gap={2} flexWrap="wrap">
                                <TextField label="Release" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
                                    value={round.release_date} onChange={(e) => setR({ release_date: e.target.value })} />
                                <TextField label="Due" type="datetime-local" size="small" InputLabelProps={{ shrink: true }}
                                    value={round.due_date} onChange={(e) => setR({ due_date: e.target.value })} />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                                The round releases — and students are emailed — at the release time.
                            </Typography>
                        </Field>

                        <Field label="Homework type">
                            <FormControl size="small" sx={{ width: 260 }}>
                                <InputLabel>Type</InputLabel>
                                <Select label="Type" value={round.question_mode} onChange={(e) => setR({ question_mode: e.target.value })}>
                                    {QUESTION_MODES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {round.question_mode === "FIXED_SET" && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                                        <TextField label="Questions in the set" type="number" size="small" sx={{ width: 170 }}
                                            value={round.set_size} onChange={(e) => setR({ set_size: e.target.value })} />
                                    </Box>
                                    <RadioGroup value={round.set_scope} onChange={(e) => setR({ set_scope: e.target.value })} sx={{ mt: 1 }}>
                                        <FormControlLabel value="COHORT" control={<Radio size="small" />}
                                            label={<Typography variant="body2">Same set for the whole class — enables the teacher wrong-question report</Typography>} />
                                        <FormControlLabel value="PER_STUDENT" control={<Radio size="small" />}
                                            label={<Typography variant="body2">Different questions per student</Typography>} />
                                    </RadioGroup>
                                </Box>
                            )}
                            {round.question_mode !== "ADAPTIVE" && (
                                <Box sx={{ mt: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                                        {round.question_mode === "FIXED_SET"
                                            ? "Pick chapters (a whole parent chapter works — the set is drawn across it):"
                                            : "Pick the leaf chapters to assign:"}
                                    </Typography>
                                    <ManualChapterPicker
                                        qbType={qbType}
                                        subjectId={subjectCourse.ett_qb_subject_id}
                                        value={round.manual_chapters}
                                        onChange={(v) => setR({ manual_chapters: v })}
                                        allowParents={round.question_mode === "FIXED_SET"}
                                        showTargets={round.question_mode === "MANUAL_TARGET"}
                                    />
                                </Box>
                            )}
                        </Field>

                        <Field label="Assign to">
                            <AssignToField
                                programUuid={programUuid}
                                rosterParams={{ program_course_id: subjectCourse.id }}
                                value={{ assign_mode: round.assign_mode, student_ids: round.student_ids }}
                                onChange={(v) => setR(v)}
                            />
                        </Field>

                        {round.question_mode !== "FIXED_SET" && (
                        <Field label="Assignment size">
                            <Box display="flex" gap={2} flexWrap="wrap">
                                {round.question_mode === "ADAPTIVE" && (
                                    <TextField label="# chapters" type="number" size="small" sx={{ width: 120 }}
                                        value={round.num_chapters} onChange={(e) => setR({ num_chapters: e.target.value })} />
                                )}
                                <TextField label="Questions / chapter" type="number" size="small" sx={{ width: 150 }}
                                    value={round.questions_per_chapter} onChange={(e) => setR({ questions_per_chapter: e.target.value })} />
                                <TextField label="Mastery" type="number" size="small" sx={{ width: 110 }}
                                    value={round.mastery_threshold} onChange={(e) => setR({ mastery_threshold: e.target.value })} />
                                <TextField label="Max passes" type="number" size="small" sx={{ width: 110 }}
                                    value={round.mastery_cycles} onChange={(e) => setR({ mastery_cycles: e.target.value })}
                                    helperText="small chapters retire after this many passes" />
                            </Box>
                        </Field>
                        )}

                        {round.question_mode === "ADAPTIVE" && (
                            <WeaknessSourceField value={round} onChange={setR} programExams={programExams} />
                        )}

                        {round.question_mode === "ADAPTIVE" && (
                        <FormControlLabel
                            control={<Checkbox checked={round.fallback_to_priority} onChange={(e) => setR({ fallback_to_priority: e.target.checked })} />}
                            label="Fall back to chapter priorities when no weakness data"
                        />
                        )}
                        <FormControlLabel
                            control={<Checkbox checked={round.notify_teachers_on_due} onChange={(e) => setR({ notify_teachers_on_due: e.target.checked })} />}
                            label="Email teachers the class's most-missed questions when the round is due"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoundOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveRound}
                        disabled={busy
                            || (round.source_mode === "EXAMS" && round.question_mode === "ADAPTIVE" && round.source_exam_ids.length === 0)
                            || (round.assign_mode === "SELECTED" && (round.student_ids?.length || 0) === 0)
                            || (round.question_mode !== "ADAPTIVE" && (round.manual_chapters?.length || 0) === 0)
                            || (round.question_mode === "FIXED_SET" && !(Number(round.set_size) > 0))}>
                        Save changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ---- Create wizard (one-off round / recurring rule, incl. rule edit) ---- */}
            {wizard && (
                <RoundWizard
                    open
                    mode={wizard.mode}
                    editRule={wizard.editRule || null}
                    programUuid={programUuid}
                    subjectCourse={subjectCourse}
                    programExams={programExams}
                    qbType={qbType}
                    onClose={() => setWizard(null)}
                    onSaved={() => { loadRecurrences(); if (onChange) onChange(); }}
                />
            )}

            {digestRound && (
                <TeacherDigestModal programUuid={programUuid} round={digestRound} onClose={() => setDigestRound(null)} />
            )}

            <ConfirmDialog ref={confirmRef} />
        </Box>
    );
};

export default HomeworkSection;
